import { useState, useMemo } from "react";
import { X, Bell, ChevronLeft } from "lucide-react";
import { useAnnouncementContext } from "../context/AnnouncementContext";
import { useProductContext } from "../context/ProductContext";
import { useUserContext } from "../context/UserContext";
import { useTranslation } from "../context/LanguageContext";
import type { Announcement } from "../services/AnnouncementService";
import { isOnlineOnlyRetailer } from "../config/retailers";
import { stripEmoji } from "../utils/alertFeed";
import { AnnouncementHero, AnnouncementRow, EmptyState, FeedRow, RowThumb, TabButton } from "./AnnouncementShared";
import { formatRelativeTime, headlineOf, type Lang } from "../utils/announcementText";
import { getProductImageUrl } from "../utils/imageUtils";

const getBrowserLanguage = (): Lang => {
  const browserLang = navigator.language || (navigator as { userLanguage?: string }).userLanguage || "en";
  return browserLang.toLowerCase().startsWith("es") ? "es" : "en";
};

type TabType = "announcements" | "alerts";

function AlertRow({ alert, lang, onClick }: { alert: Announcement; lang: Lang; onClick: () => void }) {
  const product = alert.alertProduct;
  const reason = alert.alertReason ?? "";
  const label = reason.includes("back_in_stock")
    ? <span className="font-medium text-blue-700 dark:text-blue-400">{lang === "es" ? "De nuevo en stock" : "Back in stock"}</span>
    : reason.includes("price_drop")
      ? <span className="font-medium text-green-700 dark:text-green-400">{lang === "es" ? "Bajó de precio" : "Price drop"}</span>
      : null;

  return (
    <FeedRow
      onClick={onClick}
      thumb={<RowThumb src={product?.imageUrl ? getProductImageUrl(product.imageUrl, "400") : undefined} icon={<Bell className="h-5 w-5" />} />}
      title={product?.name ? stripEmoji(product.name) : headlineOf(alert.title)}
      meta={<>{label}{label ? " · " : ""}{formatRelativeTime(alert.createdAt, lang)}</>}
    />
  );
}

export default function AnnouncementPopupModal() {
  const { selectedProduct } = useProductContext();
  const { user } = useUserContext();
  const {
    showPopupAnnouncement,
    dismissPopup,
    unseenAnnouncements,
    unseenAlertCount,
    unseenCount,
    allAlerts,
    markAllAlertsSeen,
    markAllSeen,
  } = useAnnouncementContext();
  const { currentLanguage: language } = useTranslation();
  const [selectedLang, setSelectedLang] = useState<Lang>(() => (language === "es" ? "es" : getBrowserLanguage()));
  const [activeTab, setActiveTab] = useState<TabType>("announcements");
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<Announcement | null>(null);

  const sortedAlerts = useMemo(() => [...allAlerts].sort((a, b) => b.createdAt - a.createdAt), [allAlerts]);
  const sortedAnnouncements = useMemo(() => [...unseenAnnouncements].sort((a, b) => b.createdAt - a.createdAt), [unseenAnnouncements]);

  const storeNameToRetailerKey = (store?: string): string | null => {
    if (!store) return null;
    const map: Record<string, string> = {
      walmart: "walmart", "home depot": "home_depot", "best buy": "bestbuy",
      "dollar general": "dollar_general", target: "target", woot: "woot",
    };
    return map[store.toLowerCase()] || null;
  };

  const getFirstStoreForRetailer = (retailerKey: string): string | null => {
    if (!user) return null;
    switch (retailerKey) {
      case "walmart": return user.storeIds?.[0] || null;
      case "home_depot": return user.homeDepotStoreIds?.[0] || null;
      case "dollar_general": return user.dollarGeneralStoreIds?.[0] || null;
      case "bestbuy": return user.bestbuyStoreIds?.[0] || null;
      case "target": return user.targetStoreIds?.[0] || null;
      case "lowes": return user.lowesStoreIds?.[0] || null;
      default: return user.storeIds?.[0] || null;
    }
  };

  // Don't show if no popup announcement
  if (!showPopupAnnouncement) return null;

  const es = selectedLang === "es";

  const handleClose = () => {
    dismissPopup();
    markAllSeen();
  };

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    if (tab === "alerts") markAllAlertsSeen();
  };

  const handleAlertClick = (alert: Announcement) => {
    dismissPopup();
    markAllSeen();
    const product = alert.alertProduct;
    if (!product?.upc) {
      window.location.href = `/${selectedProduct}/tracking`;
      return;
    }
    const retailer = (product.retailer || storeNameToRetailerKey(product.store) || selectedProduct).replace(/_online$/, "");
    const onlineOnly = isOnlineOnlyRetailer(retailer);
    const storeId = onlineOnly ? "online" : (product.storeId || getFirstStoreForRetailer(retailer));
    window.location.href = storeId
      ? `/${retailer}/product/${product.upc}?storeId=${storeId}`
      : `/${retailer}/product/${product.upc}`;
  };

  const earlier = sortedAnnouncements.filter((a) => a.createdAt !== showPopupAnnouncement.createdAt);
  const iconButton = "flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-[#625c75] transition hover:bg-[#f3f1f7] hover:text-[#17122b] dark:text-dark-300 dark:hover:bg-dark-700 dark:hover:text-white";
  const langButton = (lang: Lang, label: string) => (
    <button
      type="button"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setSelectedLang(lang); }}
      aria-pressed={selectedLang === lang}
      className={`h-7 rounded-md px-1.5 text-xs font-semibold transition ${selectedLang === lang ? "text-[#17122b] dark:text-white" : "text-[#b0abbd] hover:text-[#625c75] dark:text-dark-500 dark:hover:text-dark-300"}`}
    >{label}</button>
  );

  return (
    <div className="fixed inset-0 z-[10000] flex items-end justify-center bg-[#0d0a17]/50 sm:items-center sm:p-4 sheet-backdrop-in" onClick={handleClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="announcement-modal-title"
        className="relative flex max-h-[88vh] w-full flex-col overflow-hidden rounded-t-[28px] bg-white text-[#17122b] shadow-[0_20px_60px_-12px_rgba(13,10,23,0.35)] sheet-panel-in-sm sm:max-h-[82vh] sm:max-w-[440px] sm:rounded-[28px] dark:bg-dark-800 dark:text-white"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="absolute top-2 left-1/2 h-1 w-9 -translate-x-1/2 rounded-full bg-[#e4e0ec] sm:hidden dark:bg-dark-600" />

        {/* Header */}
        <header className="flex-shrink-0 border-b border-[#efedf3] px-5 pt-5 sm:pt-4 dark:border-dark-700">
          <div className="flex h-9 items-center gap-2">
            {selectedAnnouncement ? (
              <button type="button" onClick={() => setSelectedAnnouncement(null)} aria-label={es ? "Volver" : "Back"} className={`${iconButton} -ml-2`}>
                <ChevronLeft className="h-5 w-5" />
              </button>
            ) : (
              <img src="/hawk_icon_white.svg" alt="" className="h-7 w-7 rounded-lg bg-[#6d28d9] p-1" />
            )}
            <h1 id="announcement-modal-title" className="m-0 flex-1 truncate text-[17px] font-semibold tracking-[-0.01em]">
              {es ? "Novedades" : "What's new"}
            </h1>
            <div className="flex items-center">
              {langButton("en", "EN")}
              <span className="text-xs text-[#d6d2de] dark:text-dark-600" aria-hidden="true">/</span>
              {langButton("es", "ES")}
            </div>
            <button type="button" onClick={handleClose} aria-label={es ? "Cerrar" : "Close"} className={`${iconButton} -mr-2`}>
              <X className="h-5 w-5" />
            </button>
          </div>

          {selectedAnnouncement ? (
            <div className="h-3" />
          ) : (
            <div role="tablist" className="mt-2 flex gap-6">
              <TabButton active={activeTab === "announcements"} onClick={() => handleTabChange("announcements")} label={es ? "Novedades" : "Updates"} count={unseenCount} />
              <TabButton active={activeTab === "alerts"} onClick={() => handleTabChange("alerts")} label={es ? "Mis alertas" : "My alerts"} count={unseenAlertCount} />
            </div>
          )}
        </header>

        {/* Content */}
        <div className="flex-1 overflow-y-auto overscroll-contain pb-[env(safe-area-inset-bottom)]">
          {selectedAnnouncement ? (
            <AnnouncementHero key={selectedAnnouncement.createdAt} announcement={selectedAnnouncement} lang={selectedLang} />
          ) : activeTab === "announcements" ? (
            <>
              <AnnouncementHero announcement={showPopupAnnouncement} lang={selectedLang} isNew />
              {earlier.length > 0 && (
                <section className="border-t border-[#efedf3] pt-3 pb-3 dark:border-dark-700">
                  <h3 className="m-0 px-5 pb-1 text-[13px] font-semibold text-[#8a849b] dark:text-dark-400">
                    {es ? "Anteriores" : "Earlier"}
                  </h3>
                  {earlier.map((a) => (
                    <AnnouncementRow key={a.createdAt} announcement={a} lang={selectedLang} onClick={() => setSelectedAnnouncement(a)} />
                  ))}
                </section>
              )}
            </>
          ) : sortedAlerts.length > 0 ? (
            <div className="py-2">
              {sortedAlerts.map((a) => <AlertRow key={a.createdAt} alert={a} lang={selectedLang} onClick={() => handleAlertClick(a)} />)}
            </div>
          ) : (
            <EmptyState
              icon={<Bell className="h-5 w-5" />}
              title={es ? "Sin alertas de productos" : "No product alerts yet"}
              hint={es ? "Rastrea productos para recibir alertas de bajadas de precio" : "Track products to get notified of price drops"}
            />
          )}
        </div>
      </div>
    </div>
  );
}
